import React, { useState, useMemo } from 'react';
import { 
  ArrowLeftRight, 
  Search, 
  RotateCcw, 
  ArrowUpRight, 
  MinusCircle, 
  X, 
  Calendar 
} from 'lucide-react';
import { useStore } from '../../hooks/useStore.js';
import { formatDateThai } from '../../utils/date.js';
import { getBorrowUnreturnedQty } from '../../forms/return-form.js';

export function TransactionsView({ onOpenReturnModal }) {
  const transactions = useStore(s => s.transactions) || [];
  const [filterType, setFilterType] = useState('all'); // all, borrow, return, active
  const [search, setSearch] = useState('');

  const filteredTransactions = useMemo(() => {
    let list = transactions.filter(t => !t.deleted);

    if (filterType === 'borrow') {
      list = list.filter(t => t.type === 'borrow');
    } else if (filterType === 'return') {
      list = list.filter(t => t.type === 'return');
    } else if (filterType === 'active') {
      list = list.filter(t => t.type === 'borrow' && getBorrowUnreturnedQty(t) > 0);
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(t => 
        (t.itemName || '').toLowerCase().includes(q) ||
        (t.borrowerName || '').toLowerCase().includes(q) ||
        (t.notes || '').toLowerCase().includes(q) ||
        (t.operatorEmail || '').toLowerCase().includes(q)
      );
    }

    // Sort newest first
    return list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [transactions, filterType, search]);

  return (
    <div className="space-y-4 touch-scroll">
      {/* Filter and Search Bar */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl p-3 sm:p-4 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อพัสดุ, ผู้ยืม, หมายเหตุ..."
            className="w-full pl-9 pr-8 py-2 rounded-xl border border-slate-300 bg-white text-sm text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterType === 'all'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            ทั้งหมด ({transactions.filter(t => !t.deleted).length})
          </button>
          <button
            onClick={() => setFilterType('active')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterType === 'active'
                ? 'bg-amber-500 text-slate-950 shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            กำลังยืม (ยังไม่คืน)
          </button>
          <button
            onClick={() => setFilterType('borrow')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterType === 'borrow'
                ? 'bg-amber-100 text-amber-950 border border-amber-300'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            ประวัติการยืม
          </button>
          <button
            onClick={() => setFilterType('return')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterType === 'return'
                ? 'bg-emerald-100 text-emerald-950 border border-emerald-300'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            ประวัติการคืน
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-md shadow-xs overflow-hidden">
        {filteredTransactions.length === 0 ? (
          <div className="p-12 text-center text-slate-600 font-medium text-sm">
            ไม่พบประวัติรายการเคลื่อนไหวตามตัวกรองที่เลือก
          </div>
        ) : (
          <div className="table-touch-container">
            <table className="w-full text-left text-sm border-collapse min-w-[680px] lg:min-w-full">
              <thead>
                <tr className="bg-slate-100/90 border-b border-slate-300/80 text-xs font-extrabold text-slate-800 uppercase tracking-wider select-none">
                  <th className="py-2.5 px-2.5 sm:px-3.5 whitespace-nowrap">วันและเวลา</th>
                  <th className="py-2.5 px-2.5 sm:px-3.5 whitespace-nowrap">ประเภท</th>
                  <th className="py-2.5 px-2.5 sm:px-3.5">รายการพัสดุ</th>
                  <th className="py-2.5 px-2 sm:px-3 text-right">จำนวน</th>
                  <th className="py-2.5 px-2.5 sm:px-3.5">ผู้ยืม / ผู้เกี่ยวข้อง</th>
                  <th className="py-2.5 px-2.5 sm:px-3.5">หมายเหตุ</th>
                  <th className="py-2.5 px-2.5 sm:px-3.5 text-center">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransactions.map((tx) => {
                  const unreturned = tx.type === 'borrow' ? getBorrowUnreturnedQty(tx) : 0;
                  const isBorrow = tx.type === 'borrow';

                  return (
                    <tr key={tx.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-2.5 px-2.5 sm:px-3.5 whitespace-nowrap text-xs text-slate-700 font-semibold">
                        {formatDateThai(tx.timestamp)}
                      </td>
                      <td className="py-2.5 px-2.5 sm:px-3.5 whitespace-nowrap">
                        {isBorrow ? (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            unreturned > 0
                              ? 'bg-amber-100 text-amber-900 border border-amber-200'
                              : 'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}>
                            <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                            <span>ยืม {unreturned > 0 ? `(ค้าง ${unreturned})` : '(คืนครบแล้ว)'}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900 border border-emerald-200">
                            <RotateCcw className="w-3 h-3 flex-shrink-0" />
                            <span>คืน</span>
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2.5 sm:px-3.5 font-bold text-slate-950">
                        {tx.itemName}
                      </td>
                      <td className="py-2.5 px-2 sm:px-3 text-right font-extrabold text-slate-900 tabular-nums whitespace-nowrap">
                        {tx.quantity} <span className="text-xs font-semibold text-slate-600 ml-0.5">{tx.unit || 'ชิ้น'}</span>
                      </td>
                      <td className="py-2.5 px-2.5 sm:px-3.5 text-xs font-semibold text-slate-800 truncate max-w-[110px] sm:max-w-[140px]">
                        {tx.borrowerName || '-'}
                      </td>
                      <td className="py-2.5 px-2.5 sm:px-3.5 text-xs text-slate-700 font-medium truncate max-w-[100px] sm:max-w-[130px] lg:max-w-xs" title={tx.notes || ''}>
                        {tx.notes || '-'}
                      </td>
                      <td className="py-2.5 px-2.5 sm:px-3.5 text-center whitespace-nowrap">
                        {isBorrow && unreturned > 0 && (
                          <button
                            onClick={() => onOpenReturnModal(tx.id)}
                            className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-xs border border-emerald-200 transition-colors cursor-pointer"
                          >
                            <RotateCcw className="w-3 h-3" />
                            <span>คืน</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
